import type { GridApi, IRowNode } from 'ag-grid-enterprise';
import { immediateFetchGate, type GridFetchGate } from './grid-fetch-tracker';
import {
  collectRevealedFrontier,
  collectTreeExpansionSnapshot,
  type TreeNodePredicate,
} from './tree-expansion.helper';

const DEFAULT_MAX_EXPAND_DEPTH = 50;
const DEFAULT_SETTLE_TIMEOUT_MS = 30_000;
const SETTLE_POLL_ATTEMPTS = 3;

export class TreeExpansionTimeoutError extends Error {
  constructor(readonly timeoutMs: number) {
    super(`grid fetches did not settle within ${timeoutMs}ms`);
    this.name = 'TreeExpansionTimeoutError';
  }
}

export interface TreeExpansionConfig<TData> {
  isGroupNode: TreeNodePredicate<TData>;
  isProtectedRoot?: TreeNodePredicate<TData>;
  fetchGate?: GridFetchGate;
  maxDepth?: number;
  settleTimeoutMs?: number;
  onExpandError?: (error: unknown) => void;
}

export interface TreeExpansionState<TData> {
  readonly node: IRowNode<TData>;
  readonly protectedRoot: boolean;
  readonly revealedFrontier: readonly IRowNode<TData>[];
  readonly collapsibleFrontier: readonly IRowNode<TData>[];
  readonly expandedSubtree: readonly IRowNode<TData>[];
}

export class TreeExpansionController<TData> {
  private readonly isGroupNode: TreeNodePredicate<TData>;
  private readonly isProtectedRoot: TreeNodePredicate<TData>;
  private readonly fetchGate: GridFetchGate;
  private readonly maxDepth: number;
  private readonly settleTimeoutMs: number;
  private readonly onExpandError: (error: unknown) => void;
  private expandInFlight = false;

  constructor(config: TreeExpansionConfig<TData>) {
    this.isGroupNode = config.isGroupNode;
    this.isProtectedRoot = config.isProtectedRoot ?? ((): boolean => false);
    this.fetchGate = config.fetchGate ?? immediateFetchGate;
    this.maxDepth = config.maxDepth ?? DEFAULT_MAX_EXPAND_DEPTH;
    this.settleTimeoutMs = config.settleTimeoutMs ?? DEFAULT_SETTLE_TIMEOUT_MS;
    this.onExpandError =
      config.onExpandError ??
      ((error: unknown): void =>
        console.error('[TreeExpansion] expandNodeFully failed', error));
  }

  isGroup(node: IRowNode<TData>): boolean {
    return this.isGroupNode(node);
  }

  inspect(
    api: GridApi<TData>,
    node: IRowNode<TData>,
  ): TreeExpansionState<TData> {
    const snapshot = collectTreeExpansionSnapshot(api, node, this.isGroupNode);
    return {
      node,
      protectedRoot: this.isProtectedRoot(node),
      revealedFrontier: snapshot.revealedFrontier,
      collapsibleFrontier: snapshot.deepestExpandedFrontier.filter(
        (candidate) => !this.isProtectedRoot(candidate),
      ),
      expandedSubtree: snapshot.expandedSubtree,
    };
  }

  canExpandOneMoreLevel(state: TreeExpansionState<TData>): boolean {
    return state.revealedFrontier.length > 0;
  }

  expandOneMoreLevel(state: TreeExpansionState<TData>): void {
    state.revealedFrontier.forEach((child) => child.setExpanded(true));
  }

  canExpandNodeFully(state: TreeExpansionState<TData>): boolean {
    return (
      !this.expandInFlight &&
      !state.protectedRoot &&
      state.revealedFrontier.length > 0
    );
  }

  expandNodeFully(api: GridApi<TData>, state: TreeExpansionState<TData>): void {
    if (this.expandInFlight) return;
    this.expandInFlight = true;
    this.expandSubtree(api, state)
      .catch((error: unknown) => this.onExpandError(error))
      .finally(() => {
        this.expandInFlight = false;
      });
  }

  canCollapseOneLevel(state: TreeExpansionState<TData>): boolean {
    return state.collapsibleFrontier.length > 0;
  }

  collapseOneLevel(state: TreeExpansionState<TData>): void {
    state.collapsibleFrontier.forEach((child) => child.setExpanded(false));
  }

  canCollapseNodeFully(state: TreeExpansionState<TData>): boolean {
    if (state.protectedRoot) return state.expandedSubtree.length > 0;
    return Boolean(state.node.expanded);
  }

  collapseNodeFully(state: TreeExpansionState<TData>): void {
    [...state.expandedSubtree]
      .sort((a, b) => (b.level ?? 0) - (a.level ?? 0))
      .forEach((descendant) => descendant.setExpanded(false));
    if (!state.protectedRoot) state.node.setExpanded(false);
  }

  private async expandSubtree(
    api: GridApi<TData>,
    state: TreeExpansionState<TData>,
  ): Promise<void> {
    let frontier: readonly IRowNode<TData>[] = state.revealedFrontier;
    for (let depth = 0; depth < this.maxDepth; depth++) {
      if (frontier.length === 0) break;
      frontier.forEach((child) => child.setExpanded(true));
      await this.waitForExpansionToSettle();
      frontier = collectRevealedFrontier(api, state.node, this.isGroupNode);
    }
  }

  private async waitForExpansionToSettle(): Promise<void> {
    for (
      let attempt = 0;
      attempt < SETTLE_POLL_ATTEMPTS && this.fetchGate.activeFetchCount() === 0;
      attempt++
    ) {
      await this.nextMacrotask();
    }
    await this.awaitFetchGateIdle();
    await this.nextMacrotask();
  }

  private async awaitFetchGateIdle(): Promise<void> {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const timeout = new Promise<never>((_resolve, reject) => {
      timer = setTimeout(
        () => reject(new TreeExpansionTimeoutError(this.settleTimeoutMs)),
        this.settleTimeoutMs,
      );
    });
    try {
      await Promise.race([this.fetchGate.whenIdle(), timeout]);
    } finally {
      clearTimeout(timer);
    }
  }

  private nextMacrotask(): Promise<void> {
    return new Promise<void>((resolve) => setTimeout(resolve));
  }
}
